import { useMemo, useState } from 'react';
import {
  ACCESS_ROLES,
  AccessEndPicker,
  accessEndIso,
  accessEndReady,
  accessRoleLabel,
  Button,
  defaultAccessEnd,
  FUNNEL_STAGES,
  FUNNEL_STAGE_LABELS,
  HelpTip,
  Input,
} from '@dataroom/shared';
import type {
  AccessEndValue,
  AccessRole,
  CategoryNode,
  CreateAccessGrantRequest,
  FunnelStage,
  GrantWarning,
  RoomAdminRole,
} from '@dataroom/shared';
import type { RoomActor } from '@/api/hooks';
import { Modal } from './Modal';
import { GrantWarningList } from './GrantWarningList';

interface AddGrantDialogProps {
  actors: RoomActor[];
  /** The room's admin role — labels read from the buyer's side in a buyer-run room. */
  adminRole?: RoomAdminRole | null;
  /** The room's category hierarchy — the selectable tag-area scope. */
  categories: CategoryNode[];
  pending: boolean;
  /** A non-warning server/validation error, or null. */
  error: string | null;
  /** Advisory warnings from a 409 (shown with Proceed/Cancel). */
  warnings: GrantWarning[];
  onClose: () => void;
  /** Submit the grant (page adds acknowledgeWarnings on Proceed). */
  onSubmit: (body: CreateAccessGrantRequest) => void;
  onProceed: () => void;
  onCancelWarnings: () => void;
}

/**
 * Whether a grant of `role` is BUYER-side, so it must end on a date and has no
 * "Ongoing" choice (owner rule 2026-10-21). Mirrors the server's role rule; in a
 * room the buyer runs, the diligence team and guests are the buyer's own side. The
 * server also treats a person who is already a buyer here as buyer-side whatever
 * role is picked, and answers with the same plain message.
 */
function isBuyerSideRole(role: AccessRole, adminRole: RoomAdminRole | null | undefined): boolean {
  return adminRole !== 'buyer' && (role === 'diligence_team' || role === 'custom');
}

const SYNTHETIC_IDS = new Set(['uncategorized', 'demo-cat-uncat']);

interface FlatCategory {
  id: string;
  name: string;
  depth: number;
}

/** The account tree's "[Institution]" level (any case/spacing) — a checklist template, not a folder. */
function isInstitutionToken(name: string): boolean {
  return /^\s*\[\s*institution\s*\]\s*$/i.test(name);
}

/** "[Bank Account]", "[Account Name]" — a checklist placeholder, not a folder. */
function isPlaceholderName(name: string): boolean {
  return /^\s*\[[^[\]]+\]\s*$/.test(name);
}

/**
 * The grantable folders, flattened for the picker. The account tree's TEMPLATE
 * levels are not folders to grant and the server refuses them (ADR-0037 update
 * 2026-10-15): the "[Institution]" level, the statement levels right under it
 * ("[Institution] › Bank Statements") and their "[Bank Account]" placeholders.
 * A real account under them (bank unknown, "••7531") stays pickable.
 * `belowToken` is how many levels below an "[Institution]" level a node sits.
 */
function flatten(nodes: CategoryNode[], depth = 0, belowToken = 0): FlatCategory[] {
  const out: FlatCategory[] = [];
  for (const node of nodes) {
    if (SYNTHETIC_IDS.has(node.id)) continue;
    if (belowToken === 0 && isInstitutionToken(node.name)) {
      out.push(...flatten(node.children, depth, 1));
      continue;
    }
    if (belowToken === 1) {
      out.push(...flatten(node.children, depth, 2));
      continue;
    }
    if (belowToken === 2 && isPlaceholderName(node.name)) continue;
    out.push({ id: node.id, name: node.name, depth });
    out.push(...flatten(node.children, depth + 1, 0));
  }
  return out;
}

/** Why the seller's team has no folder or download choice (review H3). */
const SELLER_TEAM_WHOLE_LIBRARY =
  "The seller's team sees the whole seller library, like the seller. For limited access use Custom access.";

const selectClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

/**
 * PRD-0004 §3b advanced "Add grant" — the full grant form driving
 * POST /rooms/:roomId/access-grants, with the ADR-0010 §3 advise + acknowledge
 * flow: a 409's advisory warnings show with "Proceed anyway" (re-sends with
 * acknowledgeWarnings=true). Presentational — the page owns the mutation, the
 * pending/error state and the acknowledge re-send.
 */
export function AddGrantDialog({
  actors,
  adminRole,
  categories,
  pending,
  error,
  warnings,
  onClose,
  onSubmit,
  onProceed,
  onCancelWarnings,
}: AddGrantDialogProps) {
  const options = useMemo(() => flatten(categories), [categories]);
  const first = actors[0];
  const [actorId, setActorId] = useState(first?.id ?? '');
  const [role, setRole] = useState<AccessRole>(first?.suggestedRole ?? 'diligence_team');
  const [scope, setScope] = useState<Set<string>>(new Set());
  const [funnelStage, setFunnelStage] = useState<FunnelStage | ''>('');
  const [downloadAllowed, setDownloadAllowed] = useState(false);
  const [writeAllowed, setWriteAllowed] = useState(false);
  // Own-side roles start Ongoing as before; a buyer starts at two weeks and has
  // no Ongoing choice at all.
  const [end, setEnd] = useState<AccessEndValue>(() =>
    isBuyerSideRole(first?.suggestedRole ?? 'diligence_team', adminRole)
      ? defaultAccessEnd()
      : { choice: 'ongoing', date: '' },
  );
  const buyerSide = isBuyerSideRole(role, adminRole);
  // Switching a person to a buyer role drops a preselected "Ongoing".
  const endValue: AccessEndValue = buyerSide && end.choice === 'ongoing' ? defaultAccessEnd() : end;
  const ongoing = endValue.choice === 'ongoing';

  const hasWarnings = warnings.length > 0;
  // A seller's-team member sees the whole seller library whatever a grant names
  // (ADR-0028): no folder or download choice is offered, and the grant is the
  // whole room (an empty scope — the server writes every category).
  const sellerTeam = role === 'seller_team';
  const locked = pending || hasWarnings;

  const toggleScope = (id: string) =>
    setScope((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const canSubmit = actorId.trim().length > 0 && accessEndReady(endValue, undefined, buyerSide) && !locked;

  const submit = () => {
    const body: CreateAccessGrantRequest = {
      actorId: actorId.trim(),
      role,
      tagAreaScope: sellerTeam ? [] : [...scope],
      downloadAllowed: sellerTeam ? true : downloadAllowed,
      writeAllowed,
      ongoing,
      funnelStage: funnelStage === '' ? null : funnelStage,
    };
    const expiresAt = ongoing ? null : accessEndIso(endValue, undefined, buyerSide);
    if (expiresAt) body.expiresAt = expiresAt;
    onSubmit(body);
  };

  return (
    <Modal
      title="Manage access rights"
      description="Set exactly what a person already in the room can see and do."
      onClose={onClose}
      footer={
        hasWarnings ? (
          <div className="w-full">
            <GrantWarningList
              warnings={warnings}
              pending={pending}
              onProceed={onProceed}
              onCancel={onCancelWarnings}
            />
          </div>
        ) : (
          <>
            {error ? (
              <p className="text-sm text-destructive">{error}</p>
            ) : sellerTeam ? (
              <span className="text-xs text-muted-foreground">The whole seller library</span>
            ) : (
              <span className="text-xs text-muted-foreground">
                {scope.size} document areas selected
              </span>
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onClose} disabled={pending}>
                Cancel
              </Button>
              <Button size="sm" disabled={!canSubmit} onClick={submit}>
                {pending ? 'Saving…' : 'Save access'}
              </Button>
            </div>
          </>
        )
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="grant-actor" className="text-sm font-medium">
            Person
          </label>
          {actors.length > 0 ? (
            <select
              id="grant-actor"
              className={selectClass}
              value={actorId}
              onChange={(e) => {
                setActorId(e.target.value);
                const picked = actors.find((a) => a.id === e.target.value);
                if (picked) setRole(picked.suggestedRole);
              }}
              disabled={locked}
            >
              {actors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
          ) : (
            <Input
              id="grant-actor"
              placeholder="Actor ID (UUID)"
              value={actorId}
              onChange={(e) => setActorId(e.target.value)}
              disabled={locked}
            />
          )}
        </div>

        <div className="space-y-1.5">
          <label htmlFor="grant-role" className="text-sm font-medium">
            Access role
          </label>
          <select
            id="grant-role"
            className={selectClass}
            value={role}
            onChange={(e) => setRole(e.target.value as AccessRole)}
            disabled={locked}
          >
            {ACCESS_ROLES.map((r) => (
              <option key={r} value={r}>
                {accessRoleLabel(r, adminRole)}
              </option>
            ))}
          </select>
        </div>

        {sellerTeam ? (
          <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
            {SELLER_TEAM_WHOLE_LIBRARY}
          </p>
        ) : (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">Document areas they can see</span>
              {options.length > 0 ? (
                <span className="flex items-center gap-2 text-xs">
                  <button
                    type="button"
                    className="font-medium text-primary hover:underline disabled:opacity-50"
                    disabled={locked || scope.size === options.length}
                    onClick={() => setScope(new Set(options.map((o) => o.id)))}
                  >
                    Select all
                  </button>
                  <span className="text-muted-foreground">·</span>
                  <button
                    type="button"
                    className="font-medium text-muted-foreground hover:underline disabled:opacity-50"
                    disabled={locked || scope.size === 0}
                    onClick={() => setScope(new Set())}
                  >
                    Clear
                  </button>
                </span>
              ) : null}
            </div>
            {options.length > 0 ? (
              <ul className="max-h-40 space-y-1 overflow-y-auto rounded-md border p-2">
                {options.map((opt) => (
                  <li key={opt.id}>
                    <label
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-accent"
                      style={{ paddingLeft: opt.depth * 14 + 8 }}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={scope.has(opt.id)}
                        onChange={() => toggleScope(opt.id)}
                        disabled={locked}
                      />
                      <span className="flex-1">{opt.name}</span>
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No categories in this room yet.</p>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          <div className="flex items-center gap-1">
            <label htmlFor="grant-stage" className="text-sm font-medium">
              Unlocks when the buyer reaches
            </label>
            <HelpTip id="admin.access.unlock-stage" className="ml-1" />
          </div>
          <select
            id="grant-stage"
            className={selectClass}
            value={funnelStage}
            onChange={(e) => setFunnelStage(e.target.value as FunnelStage | '')}
            disabled={locked}
          >
            <option value="">Visible now (no gate)</option>
            {FUNNEL_STAGES.map((s) => (
              <option key={s} value={s}>
                {FUNNEL_STAGE_LABELS[s]}
              </option>
            ))}
          </select>
        </div>

        <fieldset className="space-y-2" disabled={locked}>
          <legend className="text-sm font-medium">Permissions</legend>
          {sellerTeam ? null : (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={downloadAllowed}
                onChange={(e) => setDownloadAllowed(e.target.checked)}
              />
              Allow download
            </label>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={writeAllowed}
              onChange={(e) => setWriteAllowed(e.target.checked)}
            />
            Allow editing
          </label>
        </fieldset>

        <AccessEndPicker
          value={endValue}
          onChange={setEnd}
          allowOngoing={!buyerSide}
          disabled={locked}
          legend="Duration"
        />
      </div>
    </Modal>
  );
}
